/**
 * The small markdown the app's own copy is written in.
 *
 * Not a general parser and not meant to become one — it reads the feature docs in
 * src/content/details, which are written by us and compiled into the bundle. Anything it does not
 * recognise is left alone as literal text rather than guessed at, so an unsupported flourish shows
 * up as itself on the page instead of quietly vanishing.
 *
 * It returns plain data, no JSX: the renderer builds the elements. That keeps the parsing testable
 * on its own, and it means nothing here ever produces an HTML string — so there is no escaping to
 * get wrong and no dangerouslySetInnerHTML anywhere downstream.
 *
 * Supported:
 *   # Title        the document's title, lifted out of the body — see parseMarkdown
 *   ## Heading     a heading
 *   text           a paragraph, ended by a blank line; wrapped lines join back up
 *   - item         a bullet list
 *   **bold**       inline
 *   *italic*       inline
 */

// Bold first so a single * can't claim half of a **, the same ordering the lyrics formatter in
// ViewSongPart needs for the same reason. One alternation rather than two passes, because a second
// pass over the output of the first has no way to tell text from what the first pass just produced.
const INLINE_REGEX = /\*\*([\s\S]+?)\*\*|\*([\s\S]+?)\*/g;

const HEADING_REGEX = /^(#{1,2})\s+(.*)$/;
const LIST_ITEM_REGEX = /^[-*]\s+(.*)$/;

/**
 * One line of text as a run of spans. A span is `{ text, bold, italic }` — never both, since
 * nesting is not supported and the copy has no need of it.
 */
export const parseInline = (text) => {
  const spans = [];
  let lastIndex = 0;

  // Fresh regex per call so the global flag's lastIndex never carries over between lines
  const regex = new RegExp(INLINE_REGEX);
  let match = regex.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      spans.push({ text: text.slice(lastIndex, match.index), bold: false, italic: false });
    }

    const [matched, bold, italic] = match;
    spans.push({ text: bold ?? italic, bold: bold !== undefined, italic: italic !== undefined });

    lastIndex = match.index + matched.length;
    match = regex.exec(text);
  }

  if (lastIndex < text.length) {
    spans.push({ text: text.slice(lastIndex), bold: false, italic: false });
  }

  return spans;
};

/**
 * Markdown as `{ title, blocks }`.
 *
 * A leading `# Heading` becomes `title` and is dropped from the blocks, so a document names itself
 * and whatever displays it has something to put in its header. Without one, `title` is null and the
 * caller falls back to its own — the label of the settings row the doc hangs off, in practice.
 *
 * Blocks are:
 *   { type: 'heading', spans }
 *   { type: 'paragraph', spans }
 *   { type: 'list', items: [spans] }
 */
export const parseMarkdown = (source) => {
  if (typeof source !== 'string') return { title: null, blocks: [] };

  const blocks = [];
  let paragraph = [];
  let items = null;

  // Lines are gathered as text and only parsed when the block ends, because a paragraph and a list
  // item are both joined out of however many source lines they were wrapped across
  const flushParagraph = () => {
    if (paragraph.length) blocks.push({ type: 'paragraph', spans: parseInline(paragraph.join(' ')) });
    paragraph = [];
  };

  const flushList = () => {
    if (items) blocks.push({ type: 'list', items: items.map(parseInline) });
    items = null;
  };

  source.split('\n').forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      return;
    }

    const heading = line.match(HEADING_REGEX);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', level: heading[1].length, spans: parseInline(heading[2]) });
      return;
    }

    const item = line.match(LIST_ITEM_REGEX);
    if (item) {
      flushParagraph();
      if (!items) items = [];
      items.push(item[1]);
      return;
    }

    // An indented line under an open list is the rest of the item above, wrapped. Unindented, it is
    // prose that happens to follow the list — the indent is the only thing telling the two apart,
    // so it is read off the raw line, before the trim.
    if (items && /^\s/.test(rawLine)) {
      items[items.length - 1] += ` ${line}`;
      return;
    }

    flushList();
    paragraph.push(line);
  });

  flushParagraph();
  flushList();

  // Only a level-1 heading at the very top is the title. One further down is a heading like any
  // other — a doc that opens with prose has chosen not to name itself.
  let title = null;
  if (blocks[0]?.type === 'heading' && blocks[0].level === 1) {
    title = blocks[0].spans.map((span) => span.text).join('');
    blocks.shift();
  }

  return { title, blocks };
};
